import { useTranslation } from 'react-i18next';
import { ConfirmationModal } from '@insio/ui-toolkit';
import { BillingModel } from '@insio/shared-types';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

interface CancelSubscriptionModalProps {
  open: boolean;
  loading: boolean;
  /** Localized end-of-period date (e.g. "16. července 2026"). Null while Stripe hasn't sent a period yet. */
  periodEndLabel: string | null;
  /** Billing model of the free plan; decides whether the body names documents or credits. */
  freeBillingModel: BillingModel;
  /** Document/month limit on the free plan, displayed in the body. */
  freeLimit: number;
  /** Credits/month on a free credit plan; null on a documents plan. */
  freeCredits: number | null;
  onConfirm: () => void;
  onClose: () => void;
}

export function CancelSubscriptionModal({
  open,
  loading,
  periodEndLabel,
  freeBillingModel,
  freeLimit,
  freeCredits,
  onConfirm,
  onClose
}: CancelSubscriptionModalProps) {
  const t = useFeatureTranslation('systemSettings.payments');
  const { i18n } = useTranslation();

  let message: string;
  if (freeBillingModel === BillingModel.CREDITS) {
    const count = freeCredits ?? 0;
    const credits = count.toLocaleString(i18n.language);
    message = periodEndLabel
      ? t('cancelSubscription.modal.bodyWithDateCredits', { date: periodEndLabel, count, credits })
      : t('cancelSubscription.modal.bodyWithoutDateCredits', { count, credits });
  } else {
    message = periodEndLabel
      ? t('cancelSubscription.modal.bodyWithDate', { date: periodEndLabel, freeLimit })
      : t('cancelSubscription.modal.bodyWithoutDate', { freeLimit });
  }

  return (
    <ConfirmationModal
      open={open}
      title={t('cancelSubscription.modal.title')}
      message={message}
      confirmLabel={t('cancelSubscription.modal.confirm')}
      cancelLabel={t('cancelSubscription.modal.dismiss')}
      variant='danger'
      loading={loading}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}
